import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";

const CDN = "https://vz-d0322ae1-337.b-cdn.net";
const STILL = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Sound policy: browsers only allow audible playback after the visitor has touched the page.
// Once they have (or once they tap a video), recordings entering view play with sound; leaving view pauses and mutes.
let soundUnlocked = false;
let soundWanted = true;
if (typeof window !== "undefined") {
  const unlock = () => { soundUnlocked = true; };
  ["pointerdown", "keydown", "touchend"].forEach((t) => window.addEventListener(t, unlock, { passive: true }));
}
const soundAllowed = () => soundWanted && (soundUnlocked || navigator.userActivation?.hasBeenActive === true);

async function attemptPlay(v, withSound = false) {
  v.muted = !withSound;
  if (withSound) v.volume = 1;
  try {
    await v.play();
  } catch {
    v.muted = true;
    try {
      await v.play();
    } catch {
      /* browser refused playback entirely */
    }
  }
}

const BunnyVideo = forwardRef(function BunnyVideo(
  {
    videoId,
    loop = false,
    revealDelay = 0,
    playThreshold = 0.2,
    fadeWithView = false,
    soundToggle = false,
    className = "",
    testId,
  },
  ref
) {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const posterRef = useRef(null);
  const posterFailedRef = useRef(false);
  const idRef = useRef(testId || `vid-${Math.random().toString(36).slice(2)}`);
  const audibleRef = useRef(false);
  const [armed, setArmed] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [inView, setInView] = useState(false);
  const [started, setStarted] = useState(false);
  const [muted, setMuted] = useState(true);
  const [still, setStill] = useState(STILL);
  const [hint, setHint] = useState(false);

  useImperativeHandle(ref, () => ({
    enableSound: async () => {
      const v = videoRef.current;
      if (!v) return;
      v.muted = false;
      v.volume = 1;
      try {
        await v.play();
      } catch {
        /* noop */
      }
    },
  }));

  // The player is created only once the vignette approaches the viewport.
  // Progressive MP4 first (sharp from frame one, no adaptive ladder), stepping down 1080 → 720 → 480
  // until one exists for this clip; HLS is the last resort.
  useEffect(() => {
    if (!armed) return undefined;
    const v = videoRef.current;
    const hlsSrc = `${CDN}/${videoId}/playlist.m3u8`;
    const px = (v.clientWidth || window.innerWidth) * (window.devicePixelRatio || 1);
    const rungs = [1080, 720, 480].filter((h, i) => i === 2 || px >= h * 0.8);
    let hls;
    let cancelled = false;
    let index = 0;

    const startHls = () => {
      v.removeAttribute("src");
      v.load();
      if (v.canPlayType("application/vnd.apple.mpegurl")) {
        v.src = hlsSrc;
        return;
      }
      import("hls.js").then(({ default: Hls }) => {
        if (cancelled) return;
        if (!Hls.isSupported()) {
          v.src = hlsSrc;
          return;
        }
        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
          capLevelToPlayerSize: false,
          startLevel: -1,
          autoLevelCapping: -1,
          abrEwmaDefaultEstimate: 12_000_000,
          abrEwmaDefaultEstimateMax: 12_000_000,
        });
        hls.loadSource(hlsSrc);
        hls.attachMedia(v);
        v.__hls = hls;
      });
    };
    const tryRung = () => {
      if (cancelled) return;
      if (index >= rungs.length) {
        v.removeEventListener("error", onError);
        startHls();
        return;
      }
      v.__source = `play_${rungs[index]}p.mp4`;
      v.src = `${CDN}/${videoId}/${v.__source}`;
    };
    const onError = () => { index += 1; tryRung(); };
    v.addEventListener("error", onError);
    tryRung();
    return () => {
      cancelled = true;
      v.removeEventListener("error", onError);
      if (hls) hls.destroy();
    };
  }, [armed, videoId]);

  useEffect(() => {
    const v = videoRef.current;
    const capturePoster = () => {
      const img = posterRef.current;
      if (!v || !img || !v.videoWidth) return;
      try {
        const c = document.createElement("canvas");
        c.width = v.videoWidth;
        c.height = v.videoHeight;
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        img.src = c.toDataURL("image/jpeg", 0.94);
      } catch {
        /* cross-origin tainted — keep whatever poster we have */
      }
    };
    const onPlaying = () => setStarted(true);
    const onVolume = () => setMuted(v.muted);
    // Broadcast whether this video is actually producing sound so the ambient
    // track can duck (unmuted + playing) and swell back (muted or paused).
    const syncAudible = () => {
      const audible = !v.muted && !v.paused;
      if (audible !== audibleRef.current) {
        audibleRef.current = audible;
        window.dispatchEvent(
          new CustomEvent("prism:video-sound", { detail: { id: idRef.current, muted: !audible } })
        );
      }
    };
    // If the Bunny thumbnail can't load (e.g. hotlink-protected domain),
    // substitute the real first video frame at native resolution.
    const onLoaded = () => {
      if (posterFailedRef.current) capturePoster();
    };
    const onClaim = (e) => {
      if (e.detail?.id !== idRef.current && !v.muted) v.muted = true;
    };
    const claim = () => {
      if (!v.muted && !v.paused) window.dispatchEvent(new CustomEvent("prism:video-claim", { detail: { id: idRef.current } }));
    };
    window.addEventListener("prism:video-claim", onClaim);
    v.addEventListener("playing", claim);
    v.addEventListener("volumechange", claim);
    v.__capturePoster = capturePoster;
    v.addEventListener("playing", onPlaying);
    v.addEventListener("volumechange", onVolume);
    v.addEventListener("loadeddata", onLoaded);
    v.addEventListener("playing", syncAudible);
    v.addEventListener("pause", syncAudible);
    v.addEventListener("volumechange", syncAudible);
    v.addEventListener("ended", syncAudible);
    return () => {
      window.removeEventListener("prism:video-claim", onClaim);
      v.removeEventListener("playing", claim);
      v.removeEventListener("volumechange", claim);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("volumechange", onVolume);
      v.removeEventListener("loadeddata", onLoaded);
      v.removeEventListener("playing", syncAudible);
      v.removeEventListener("pause", syncAudible);
      v.removeEventListener("volumechange", syncAudible);
      v.removeEventListener("ended", syncAudible);
      if (audibleRef.current) {
        window.dispatchEvent(
          new CustomEvent("prism:video-sound", { detail: { id: idRef.current, muted: true } })
        );
      }
    };
  }, []);

  useEffect(() => {
    const wrap = wrapRef.current;
    const v = videoRef.current;
    let revealTimer;

    const armIo = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setArmed(true);
          armIo.disconnect();
        }
      },
      { rootMargin: "600px 0px" }
    );
    armIo.observe(wrap);

    const revealIo = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && e.intersectionRatio >= 0.2) {
          revealTimer = setTimeout(() => setRevealed(true), revealDelay);
          revealIo.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    revealIo.observe(wrap);

    const playIo = new IntersectionObserver(
      ([e]) => {
        const active = e.isIntersecting && e.intersectionRatio >= playThreshold;
        setInView(active);
        if (active && !STILL()) {
          attemptPlay(v, soundToggle && soundAllowed()).then(() => {
            if (soundToggle && v.muted && !v.paused) setHint(true);
          });
        } else if (!active) {
          v.pause();
          v.muted = true;
          setHint(false);
        }
      },
      { threshold: [0, playThreshold, 0.6] }
    );
    playIo.observe(wrap);

    return () => {
      armIo.disconnect();
      revealIo.disconnect();
      playIo.disconnect();
      clearTimeout(revealTimer);
    };
  }, [revealDelay, playThreshold, soundToggle]);

  // A missing MP4 can reject the first play before the HLS fallback is ready.
  useEffect(() => {
    if (!inView || still) return undefined;
    const v = videoRef.current;
    let disposed = false;
    const playReady = () => {
      if (!v.paused || document.visibilityState === "hidden") return;
      attemptPlay(v, soundToggle && soundAllowed()).then(() => {
        if (!disposed && soundToggle && v.muted && !v.paused) setHint(true);
      });
    };
    v.addEventListener("canplay", playReady);
    if (v.readyState >= 3) playReady();
    return () => { disposed = true; v.removeEventListener("canplay", playReady); };
  }, [inView, still, soundToggle]);

  // A tap anywhere on the frame: play with sound. A second tap: quiet again.
  const toggleSound = async (e) => {
    e?.stopPropagation?.();
    const v = videoRef.current;
    if (!v) return;
    soundUnlocked = true;
    setStill(false);
    setArmed(true);
    setHint(false);
    const wantSound = v.muted || v.paused;
    soundWanted = wantSound;
    v.muted = !wantSound;
    if (wantSound) v.volume = 1;
    try {
      await v.play();
    } catch {
      /* noop */
    }
    setMuted(v.muted);
  };

  const playStill = (e) => {
    e?.stopPropagation?.();
    soundUnlocked = true;
    setStill(false);
    setArmed(true);
    attemptPlay(videoRef.current, soundToggle);
  };

  const opacity = fadeWithView ? (inView && revealed ? 1 : 0) : revealed ? 1 : 0;

  return (
    <div
      ref={wrapRef}
      data-testid={testId}
      data-sound={soundToggle ? (muted ? "muted" : "on") : undefined}
      onClick={soundToggle ? toggleSound : undefined}
      role={soundToggle ? "button" : undefined}
      tabIndex={soundToggle ? 0 : undefined}
      onKeyDown={soundToggle ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleSound(e); } } : undefined}
      aria-label={soundToggle ? (muted ? "Play with sound" : "Mute") : undefined}
      className={`video-container ${soundToggle ? "video-container--tappable" : ""} ${className}`}
      style={{
        opacity,
        transition: `opacity ${fadeWithView ? 500 : 550}ms cubic-bezier(0.16, 1, 0.3, 1)`,
        willChange: "opacity",
      }}
    >
      <video
        ref={videoRef}
        data-testid={`${idRef.current}-media`}
        playsInline
        webkit-playsinline="true"
        muted
        loop={loop}
        preload="metadata"
        aria-label="PrismPixel laboratory recording"
      />
      <img
        ref={posterRef}
        src={`${CDN}/${videoId}/thumbnail.jpg`}
        alt=""
        aria-hidden="true"
        width={1280}
        height={720}
        loading="lazy"
        decoding="async"
        onError={() => {
          posterFailedRef.current = true;
          videoRef.current?.__capturePoster?.();
        }}
        className="video-poster"
        style={{
          opacity: started ? 0 : 1,
          transition: "opacity 700ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      />
      {still && !started && (
        <button
          type="button"
          data-cursor
          data-testid={`${idRef.current}-play`}
          onClick={playStill}
          aria-label="Play laboratory recording"
          className="video-sound-toggle !left-1/2 !top-1/2 !bottom-auto !right-auto -translate-x-1/2 -translate-y-1/2 !h-14 !w-14 rounded-full"
        >
          <Play size={20} className="ml-0.5 text-[#84DFF4]" fill="#84DFF4" strokeWidth={0} />
        </button>
      )}
      {soundToggle && (
        <button
          type="button"
          data-cursor
          data-testid={`${idRef.current}-sound`}
          onClick={toggleSound}
          aria-pressed={!muted}
          aria-label={muted ? "Play with sound" : "Mute"}
          className={`video-sound-toggle ${hint && muted ? "video-sound-toggle--hint" : ""}`}
        >
          {muted ? (
            <VolumeX size={18} className="text-[#84DFF4]" strokeWidth={1.5} />
          ) : (
            <Volume2 size={18} className="text-[#84DFF4]" strokeWidth={1.5} />
          )}
          <span data-testid={`${idRef.current}-sound-label`} className="video-sound-toggle__label">
            {muted ? "Tap for sound" : "Sound on"}
          </span>
        </button>
      )}
    </div>
  );
});

export default BunnyVideo;
